import { Coins } from 'lucide-react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSettleManagementFeeThrough } from '@/hooks/react-query/useManagementFees';
import {
  FUND_TRANSACTION_TYPE_LABELS,
  type FundTransactionDetail,
  type ReconciliationStateResponse,
} from '@/types/fund-transactions';
import {
  MANAGEMENT_FEE_CALCULATION_BASIS_LABELS,
  MANAGEMENT_FEE_SETTLEMENT_LABELS,
  ManagementFeeSettlement,
} from '@/types/management-fees';
import {
  formatCurrency,
  formatDate,
  formatMonthYear,
} from '@/utils/formatters';

import { Field } from './transactionDetails';
import { TransactionDetailsCard } from './TransactionDetailsCard';

export function ManagementFeeDetailsCard({
  transaction,
  fundId,
  fundName,
  reconciliation,
  onViewJournalEntries,
}: {
  transaction: FundTransactionDetail;
  fundId: string;
  fundName: string;
  reconciliation: ReconciliationStateResponse | undefined;
  onViewJournalEntries?: () => void;
}) {
  const snapshot = transaction.management_fee;

  return (
    <TransactionDetailsCard
      transaction={transaction}
      fundName={fundName}
      reconciliation={reconciliation}
      onViewJournalEntries={onViewJournalEntries}
      icon={<Coins className="h-5 w-5" />}
      iconClassName="bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-300"
      eyebrow={FUND_TRANSACTION_TYPE_LABELS[transaction.transaction_type]}
      fields={
        <>
          <Field label="Amount">
            {formatCurrency(transaction.amount, {
              currency: transaction.currency,
            })}
          </Field>
          {snapshot?.recognition_month && (
            <Field label="Month">
              {formatMonthYear(snapshot.recognition_month)}
            </Field>
          )}
          {snapshot && (
            <Field label={snapshot.recognition_month ? 'Fee period' : 'Period'}>
              {`${formatDate(snapshot.period_start)} – ${formatDate(snapshot.period_end)}`}
            </Field>
          )}
          {snapshot && (
            <Field label="Fee basis">
              {
                MANAGEMENT_FEE_CALCULATION_BASIS_LABELS[
                  snapshot.fee_basis as keyof typeof MANAGEMENT_FEE_CALCULATION_BASIS_LABELS
                ]
              }
            </Field>
          )}
          {snapshot && (
            <Field label="Annual rate">
              {`${(snapshot.annual_rate * 100).toFixed(2)}%`}
            </Field>
          )}
          {snapshot && !snapshot.recognition_month && (
            <Field label="Settled through">
              <SettlementField
                fundId={fundId}
                transaction={transaction}
                settledThrough={snapshot.settled_through}
              />
            </Field>
          )}
        </>
      }
    />
  );
}

function SettlementField({
  fundId,
  transaction,
  settledThrough,
}: {
  fundId: string;
  transaction: FundTransactionDetail;
  settledThrough: ManagementFeeSettlement;
}) {
  const settle = useSettleManagementFeeThrough(fundId, transaction.id);

  if (transaction.status !== 'DRAFT') {
    return MANAGEMENT_FEE_SETTLEMENT_LABELS[settledThrough];
  }

  return (
    <Select
      value={settledThrough}
      onValueChange={(value) => settle.mutate(value as ManagementFeeSettlement)}
      disabled={settle.isPending}
    >
      <SelectTrigger className="w-56" aria-label="Settled through">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.values(ManagementFeeSettlement).map((settlement) => (
          <SelectItem key={settlement} value={settlement}>
            {MANAGEMENT_FEE_SETTLEMENT_LABELS[settlement]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
